import type { ReactNode } from "react"
import { Check } from "lucide-react"
import { cn } from "@/common/lib/utils"

/**
 * Shared numbered-circle wizard stepper (migrosbank design system).
 *
 * Progress-fill model: every reached step (≤ `current`) is filled with the primary color; steps
 * before the current one show a check, the current step shows its number, and future steps use the
 * subtle background. Spacing is left to the caller via `className` so each wizard can tune it.
 *
 * Generic over any wizard — pass `steps` as resolved (already-localized) labels. An optional
 * `finalStep` renders a trailing terminal "done" circle (e.g. "Fertig") that fills once every step
 * is complete.
 */
export interface WizardStepperProps {
    /** Ordered, already-localized step labels. */
    steps: string[]
    /** 1-based index of the current step. */
    current: number
    /** Optional trailing terminal step, reached once `current > steps.length`. */
    finalStep?: string
    className?: string
}

function StepItem({ label, active, circle }: { label: string; active: boolean; circle: ReactNode }) {
    return (
        <div className="flex min-w-[88px] max-w-[280px] flex-1 basis-0 flex-col items-center gap-2 text-center">
            <div
                className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium",
                    active ? "bg-primary text-primary-foreground" : "bg-primary-subtle1 text-primary",
                )}
            >
                {circle}
            </div>
            <span className={cn("text-sm text-primary", active ? "font-medium" : "font-normal")}>{label}</span>
        </div>
    )
}

export default function WizardStepper({ steps, current, finalStep, className }: WizardStepperProps) {
    return (
        <div className={cn("mx-auto flex w-full max-w-[950px] items-start justify-center gap-6", className)}>
            {steps.map((label, idx) => {
                const num = idx + 1
                return (
                    <StepItem
                        key={num}
                        label={label}
                        active={current >= num}
                        circle={current > num ? <Check className="h-4 w-4" /> : num}
                    />
                )
            })}
            {finalStep !== undefined && (
                <StepItem label={finalStep} active={current >= steps.length + 1} circle={<Check className="h-4 w-4" />} />
            )}
        </div>
    )
}
